import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHistory, FaPlus, FaUser, FaDownload, FaEllipsisH, FaBolt, FaTimes, FaTag, FaBarcode, FaPhone, FaUserMinus, FaUserPlus, FaPrint } from 'react-icons/fa';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import TimelineView from './TimelineView';
import AssignmentTimeline from './AssignmentTimeline';
import EquipmentLifeIndicator from './EquipmentLifeIndicator';
import InventoryAISummary from './InventoryAISummary';
import TableView from './TableView';
import axiosInstance from '../utils/axiosConfig';
import { getApiError, getBlobApiError } from '../utils/apiError';
import './AssetsView.css';
import AddTabletModal from './AddTabletModal';
import AssignTabletModal from './AssignTabletModal';
import EmployeeModal from './EmployeeModal';
import DeleteTabletModal from './DeleteTabletModal';
import TabletModal from './TabletModal';
import BrandLogo from './BrandLogo';
import Modal from './common/Modal';
import { QuickActionsMenu } from './common';

function TabletsView() {
  const [showTimeline, setShowTimeline] = useState(false);
  const [showIndividualTimeline, setShowIndividualTimeline] = useState(false);
  const [selectedTabletForTimeline, setSelectedTabletForTimeline] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [selectedTablet, setSelectedTablet] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [motivoDesasignar, setMotivoDesasignar] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    activo_fijo: true,
    marca: true,
    modelo: true,
    serial: true,
    imei: true,
    linea: false,
    estado: true,
    empleado: false,
    fecha_asignacion: false,
    vida_util: false,
  });

  const [showConfirmConstanciaModal, setShowConfirmConstanciaModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTabletModal, setShowTabletModal] = useState(false);
  const [selectedTabletForView, setSelectedTabletForView] = useState(null);
  const [showAssignmentTimeline, setShowAssignmentTimeline] = useState(false);
  const [selectedForAssignments, setSelectedForAssignments] = useState(null);
  const [showAISummary, setShowAISummary] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);

  const handleCloseEmployeeModal = () => {
    setShowEmployeeModal(false);
    setSelectedEmployee(null);
  };

  const availableFilters = [
    { id: 'modelo', placeholder: 'Todos los modelos' },
    { id: 'estado', placeholder: 'Todos los estados' }
  ];

  useEffect(() => {
    // Defer data fetching to allow page transition animations to finish smoothly
    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/tablets');
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar tablets:', err);
      setError('Error al cargar la lista de tablets');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSuccess = () => {
    fetchData();
    setShowAssignModal(false);
    setSelectedTablet(null);
  };

  const handleUnassignClick = (tablet) => {
    setSelectedTablet(tablet);
    setMotivoDesasignar('');
    setShowUnassignConfirm(true);
  };

  const closeUnassignConfirm = () => {
    setShowUnassignConfirm(false);
    setSelectedTablet(null);
  };

  const handleUnassign = async () => {
    if (!selectedTablet || unassigning) return;
    setUnassigning(true);
    try {
      await axiosInstance.post(`/api/tablets/${selectedTablet.id}/desasignar`, {
        motivo: motivoDesasignar.trim()
      });
      await fetchData();
      closeUnassignConfirm();
    } catch (err) {
      console.error('Error al desasignar tablet:', err);
      alert(getApiError(err, 'Error al desasignar la tablet'));
    } finally {
      setUnassigning(false);
    }
  };

  const handleAddTablet = (newTablet) => {
    setData(prev => [newTablet, ...prev]);
  };

  const handleEmployeeClick = async (empleadoId) => {
    try {
      const empleadoResponse = await axiosInstance.get(`/api/empleados/${empleadoId}`);
      setSelectedEmployee(empleadoResponse.data);
      setShowEmployeeModal(true);
    } catch (error) {
      console.error('Error al obtener datos del empleado:', error);
    }
  };

  const handleView = (tablet) => {
    setSelectedTabletForView(tablet);
    setShowTabletModal(true);
  };

  // Las notas editadas en el modal se reflejan en la fila sin recargar
  const handleTabletUpdate = (updatedTablet) => {
    setData(prev => prev.map(item => (item.id === updatedTablet.id ? updatedTablet : item)));
    setSelectedTabletForView(updatedTablet);
  };

  const handleGenerateConstancia = async (tablet) => {
    try {
      const response = await axiosInstance.get(
        `/api/tablets/${tablet.id}/constancia`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `constancia_tablet_${tablet.empleado}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      toast.error(await getBlobApiError(error, 'Error al generar la constancia'));
    }
  };

  const handleSave = async (id, field, value) => {
    try {
      console.log('handleSave called:', { id, field, value });
      const response = await axiosInstance.patch(`/api/tablets/${id}`, { [field]: value });
      console.log('API Response:', response.data);

      // El backend responde { message }, no { success }: cualquier 2xx es un guardado exitoso.
      setData(old =>
        old.map(row =>
          row.id === id
            ? { ...row, [field]: value }
            : row
        )
      );
      return true;
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast.error(getApiError(error, 'No se pudo guardar el cambio'));
      return false;
    }
  };

  const EditableCell = ({ value, column, row, onSave, isBadgeType = false, isPhoneType = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
      console.log('Value changed:', { value, currentValue, column: column.id });
      setCurrentValue(value);
    }, [value]);

    const handleClick = (e) => {
      if ((isBadgeType || isPhoneType) && value) {
        const copyToClipboard = (text) => {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text);
          }

          return new Promise((resolve, reject) => {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
              document.execCommand('copy');
              document.body.removeChild(textarea);
              resolve();
            } catch (err) {
              document.body.removeChild(textarea);
              reject(err);
            }
          });
        };

        copyToClipboard(value).then(() => {
          const badge = e.currentTarget.closest('.badge-cell');
          if (badge) {
            badge.classList.add('copied');
            setTimeout(() => {
              badge.classList.remove('copied');
            }, 300);
          }
        }).catch(err => {
          console.error('Error al copiar:', err);
        });
      }
    };

    const handleDoubleClick = () => {
      console.log('Double click, starting edit mode');
      setIsEditing(true);
    };

    const handleInputChange = (e) => {
      console.log('Input value changed:', e.target.value);
      setCurrentValue(e.target.value);
    };

    const handleChange = async (e) => {
      const newValue = e.target.value;
      console.log('handleChange called:', { newValue, field: column.id });

      try {
        console.log('Attempting to save:', { id: row.original.id, field: column.id, value: newValue });
        const success = await onSave(row.original.id, column.id, newValue);
        console.log('Save result:', success);

        if (success) {
          setCurrentValue(newValue);
        } else {
          console.log('Save failed, reverting to:', value);
          setCurrentValue(value);
        }
      } catch (error) {
        console.error('Error in handleChange:', error);
        setCurrentValue(value);
      }
      setIsEditing(false);
    };

    const handleBlur = () => {
      console.log('Input/Select blurred');
      setIsEditing(false);
    };

    if (isEditing) {
      return (
        <input
          type="text"
          value={currentValue || ''}
          onChange={handleInputChange}
          onBlur={handleChange}
          autoFocus
          className="editable-cell-input"
        />
      );
    }

    return (
      <div
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`editable-cell ${isBadgeType ? 'badge-type' : ''} ${isPhoneType ? 'phone-type' : ''}`}
        title={(isBadgeType || isPhoneType) ? `Click para copiar, doble click para editar` : 'Doble click para editar'}
      >
        {value || 'Sin asignar'}
      </div>
    );
  };

  const columns = useMemo(() => [
    {
      header: 'Activo Fijo',
      accessorKey: 'activo_fijo',
      cell: ({ row, column, table }) => (
        <div className="badge-cell activo-fijo-badge">
          <FaTag className="badge-icon" />
          <EditableCell
            value={row.original.activo_fijo}
            column={column}
            row={row}
            table={table}
            onSave={handleSave}
            isBadgeType={true}
          />
        </div>
      )
    },
    {
      header: 'Marca',
      accessorKey: 'marca',
      cell: ({ row }) => (
        <div className="brand-cell">
          <BrandLogo brand={row.original.marca} />
        </div>
      )
    },
    {
      header: 'Modelo',
      accessorKey: 'modelo',
      cell: ({ row, column, table }) => (
        <EditableCell
          value={row.original.modelo}
          column={column}
          row={row}
          table={table}
          onSave={handleSave}
        />
      )
    },
    {
      header: 'Serial',
      accessorKey: 'serial',
      cell: ({ row, column, table }) => (
        <div className="badge-cell serial-badge">
          <FaBarcode className="badge-icon" />
          <EditableCell
            value={row.original.serial}
            column={column}
            row={row}
            table={table}
            onSave={handleSave}
            isBadgeType={true}
          />
        </div>
      )
    },
    {
      header: 'IMEI',
      accessorKey: 'imei',
      cell: ({ row, column, table }) => (
        <EditableCell
          value={row.original.imei}
          column={column}
          row={row}
          table={table}
          onSave={handleSave}
        />
      )
    },
    {
      header: 'Línea',
      accessorKey: 'linea',
      cell: ({ row, column, table }) => {
        const linea = row.original.linea;

        return (
          <div className="phone-cell">
            <a
              href={`tel:${linea}`}
              className="phone-icon-link"
              title="Llamar"
              onClick={(e) => e.stopPropagation()}
            >
              <FaPhone className="phone-icon" />
            </a>
            <EditableCell
              value={linea}
              column={column}
              row={row}
              table={table}
              onSave={handleSave}
              isPhoneType={true}
            />
          </div>
        );
      }
    },
    {
      header: 'Estado', accessorKey: 'estado', cell: ({ row }) => {
        const estado = row.original.estado?.toLowerCase() || '';
        const empleado = row.original.empleado;
        const manual = row.original.asignacion_manual;
        const empleadoId = row.original.empleado_id;

        if (estado === 'asignado' && empleado) {
          return (
            <span
              className="employee-type"
              onClick={() => handleEmployeeClick(empleadoId)}
              title={empleado}
            >
              <FaUser className="employee-icon" />
              <span className="employee-name">{empleado}</span>
            </span>
          );
        }

        if (estado === 'asignado' && !empleado && manual) {
          return (
            <span className="manual-badge" title={manual}>
              <FaUser className="employee-icon" />
              {manual}
            </span>
          );
        }

        return (
          <span className={`estado-badge ${estado.replace(/\s+/g, '-')}`}>
            {row.original.estado}
          </span>
        );
      }
    },
    {
      header: 'Empleado', accessorKey: 'empleado', cell: ({ row }) => {
        const empleado = row.original.empleado || row.original.asignacion_manual;
        const empleadoId = row.original.empleado_id;

        if (!empleado) return <span className="no-employee">Sin asignar</span>;

        return (
          <div
            className="employee-type clickable"
            onClick={() => handleEmployeeClick(empleadoId)}
            style={{ cursor: 'pointer' }}
          >
            {row.original.empleado ? <FaUser className="employee-icon" /> : null}
            <span className="employee-name">{empleado}</span>
          </div>
        );
      }
    },
    { header: 'Fecha de Asignación', accessorKey: 'fecha_asignacion', cell: ({ row }) => row.original.fecha_asignacion ? new Date(row.original.fecha_asignacion).toLocaleDateString('es-ES') : <span className="no-employee">Sin asignar</span> },
    {
      header: 'Vida útil',
      id: 'vida_util',
      cell: ({ row }) => (
        <EquipmentLifeIndicator createdAt={row.original.created_at} compact />
      ),
    },
    {
      header: 'Acciones',
      id: 'acciones',
      cell: ({ row }) => (
        <div className={`action-buttons ${activeActionMenu === row.original.id ? 'menu-active' : ''}`}>
          <button
            className="action-button view-button"
            title="Ver detalles"
            onClick={() => handleView(row.original)}
          >
            <FaEllipsisH />
          </button>

          <button
            className="action-button history-button"
            onClick={() => {
              setSelectedForAssignments(row.original);
              setShowAssignmentTimeline(true);
            }}
            title="Historial de asignaciones"
          >
            <FaHistory />
          </button>

          <div className="quick-actions-container" style={{ display: 'flex', alignItems: 'center' }}>
            <button
              className={`icon-button ${activeActionMenu === row.original.id ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (activeActionMenu === row.original.id) {
                  setActiveActionMenu(null);
                  setMenuAnchorRect(null);
                } else {
                  setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                  setActiveActionMenu(row.original.id);
                }
              }}
              title="Acciones rápidas"
            >
              <FaBolt />
            </button>
            <AnimatePresence>
              {activeActionMenu === row.original.id && (
                <QuickActionsMenu anchorRect={menuAnchorRect}>
                  {[
                    {
                      icon: (row.original.empleado || row.original.asignacion_manual) ? FaUserMinus : FaUserPlus,
                      label: (row.original.empleado || row.original.asignacion_manual) ? 'Desasignar tablet' : 'Asignar tablet',
                      color: (row.original.empleado || row.original.asignacion_manual) ? '#EF4444' : '#10B981',
                      onClick: () => {
                        if (row.original.empleado || row.original.asignacion_manual) {
                          handleUnassignClick(row.original);
                        } else {
                          setSelectedTablet(row.original);
                          setShowAssignModal(true);
                        }
                        setActiveActionMenu(null);
                      }
                    },
                    ...((row.original.empleado || row.original.asignacion_manual) ? [{
                      icon: FaPrint,
                      label: 'Imprimir constancia',
                      color: '#3B82F6',
                      onClick: () => {
                        setShowConfirmConstanciaModal(true);
                        setSelectedTablet(row.original);
                        setActiveActionMenu(null);
                      }
                    }] : [])
                  ].map((action) => (
                    <button
                      key={action.label}
                      className="quick-action-item"
                      onClick={action.onClick}
                    >
                      <span className="action-icon" style={{ color: action.color }}>
                        <action.icon />
                      </span>
                      <span className="action-label">{action.label}</span>
                    </button>
                  ))}
                </QuickActionsMenu>
              )}
            </AnimatePresence>
          </div>

          <button
            className="action-button delete-button"
            title="Cambiar estado"
            onClick={() => {
              setSelectedTablet(row.original);
              setShowDeleteModal(true);
            }}
          >
            <FaTimes />
          </button>
        </div>
      ),
    }
  ], [activeActionMenu]);

  const handleExportToCSV = () => {
    try {
      const visibleColumns = columns
        .filter(col => columnVisibility[col.accessorKey || col.id])
        .map(col => ({
          key: col.accessorKey || col.id,
          header: typeof col.header === 'string' ? col.header : col.accessorKey || col.id
        }));

      // Se exportan las filas que quedan tras la búsqueda y los filtros de la tabla
      const rows = filteredRows;

      // Agregar columna de comentarios si hay notas
      const exportColumns = [...visibleColumns];
      const hasNotas = rows.some(row => row.notas);
      if (hasNotas) {
        exportColumns.push({ key: 'notas', header: 'Comentarios' });
      }

      let csvContent = exportColumns.map(col => `"${col.header}"`).join(',') + '\n';

      rows.forEach(row => {
        const rowData = exportColumns.map(col => {
          let value = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : '';

          // Si es la columna de estado y está asignado, mostrar el nombre del empleado
          if (col.key === 'estado' && row.estado?.toLowerCase() === 'asignado') {
            if (row.empleado) {
              value = row.empleado;
            } else if (row.asignacion_manual) {
              value = row.asignacion_manual;
            }
          }

          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowData.join(',') + '\n';
      });

      // El BOM (\uFEFF) le indica a Excel que el archivo es UTF-8; sin él, las tildes salen rotas
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'Tablets.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar a CSV:', error);
      alert('Error al exportar a CSV. Por favor, inténtelo de nuevo.');
    }
  };

  return (
    <div className="assets-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Tablets ({filteredCount})</h2>
        <div className="flex gap-3 items-center">
          <button
            className="btn-secondary"
            onClick={() => setShowAISummary(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7c3aed', borderColor: '#c4b5fd' }}
          >
            <Sparkles size={14} />
            Resumen IA
          </button>
          <button className="btn-secondary" onClick={() => setShowTimeline(true)}>
            <FaHistory />
            Historial
          </button>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Nuevo Tablet
          </button>
        </div>
      </div>

      <TableView
        data={data}
        columns={columns}
        loading={loading}
        error={error}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        defaultPageSize={30}
        defaultSorting={[{ id: 'activo_fijo', desc: true }]}
        availableFilters={availableFilters}
        activeActionMenu={activeActionMenu}
        setActiveActionMenu={setActiveActionMenu}
        onFilteredCountChange={setFilteredCount}
        onFilteredRowsChange={setFilteredRows}
      />

      <div className="flex justify-end mt-4">
        <button
          className="btn-secondary"
          onClick={handleExportToCSV}
          disabled={loading || filteredRows.length === 0}
        >
          <FaDownload /> Exportar a Excel
        </button>
      </div>

      {showTimeline && (
        <TimelineView
          categoria="tablets"
          onClose={() => setShowTimeline(false)}
        />
      )}

      {showIndividualTimeline && selectedTabletForTimeline && (
        <TimelineView
          categoria="tablets"
          itemId={selectedTabletForTimeline.id}
          itemName={`${selectedTabletForTimeline.marca} ${selectedTabletForTimeline.modelo}`}
          onClose={() => {
            setShowIndividualTimeline(false);
            setSelectedTabletForTimeline(null);
          }}
        />
      )}

      {showAddModal && (
        <AddTabletModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddTablet}
        />
      )}

      {showAssignModal && selectedTablet && (
        <AssignTabletModal
          tablet={selectedTablet}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTablet(null);
          }}
          onAssign={handleAssignSuccess}
        />
      )}

      <Modal
        isOpen={showUnassignConfirm && !!selectedTablet}
        onClose={closeUnassignConfirm}
        size="sm"
      >
        <Modal.Header onClose={closeUnassignConfirm}>
          Confirmar Desasignación
        </Modal.Header>
        <Modal.Body>
          <p>¿Está seguro que desea desasignar la siguiente tablet?</p>
          <div className="asset-info">
            <p><strong>Marca:</strong> {selectedTablet?.marca}</p>
            <p><strong>Modelo:</strong> {selectedTablet?.modelo}</p>
            <p><strong>Asignada a:</strong> {selectedTablet?.empleado || selectedTablet?.asignacion_manual}</p>
          </div>
          <div className="mt-4">
            <label htmlFor="motivo-desasignar-tablet" className="block text-sm font-medium text-gray-700 mb-1">
              Motivo (opcional)
            </label>
            <textarea
              id="motivo-desasignar-tablet"
              value={motivoDesasignar}
              onChange={(e) => setMotivoDesasignar(e.target.value)}
              rows={2}
              placeholder="Por qué se retira el equipo"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-sura-blue focus:border-transparent"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={closeUnassignConfirm} className="btn-secondary" disabled={unassigning}>
            Cancelar
          </button>
          <button onClick={handleUnassign} className="btn-primary" disabled={unassigning}>
            {unassigning ? 'Desasignando...' : 'Confirmar'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={showConfirmConstanciaModal && !!selectedTablet}
        onClose={() => setShowConfirmConstanciaModal(false)}
        size="sm"
      >
        <Modal.Header onClose={() => setShowConfirmConstanciaModal(false)}>
          Confirmar Impresión
        </Modal.Header>
        <Modal.Body>
          <p>¿Desea imprimir la constancia de entrega para la tablet?</p>
          <div className="asset-info">
            <p><strong>Marca:</strong> {selectedTablet?.marca}</p>
            <p><strong>Modelo:</strong> {selectedTablet?.modelo}</p>
            <p><strong>Empleado:</strong> {selectedTablet?.empleado}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => setShowConfirmConstanciaModal(false)} className="btn-secondary">
            Cancelar
          </button>
          <button
            onClick={() => {
              handleGenerateConstancia(selectedTablet);
              setShowConfirmConstanciaModal(false);
            }}
            className="btn-primary"
          >
            Imprimir
          </button>
        </Modal.Footer>
      </Modal>

      {showEmployeeModal && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={handleCloseEmployeeModal}
        />
      )}

      {showDeleteModal && selectedTablet && (
        <DeleteTabletModal
          tablet={selectedTablet}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedTablet(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowDeleteModal(false);
            setSelectedTablet(null);
          }}
        />
      )}

      {showTabletModal && selectedTabletForView && (
        <TabletModal
          tablet={selectedTabletForView}
          onClose={() => {
            setShowTabletModal(false);
            setSelectedTabletForView(null);
          }}
          onUpdate={handleTabletUpdate}
        />
      )}

      {showAssignmentTimeline && selectedForAssignments && (
        <AssignmentTimeline
          tipo="tablet"
          equipoId={selectedForAssignments.id}
          equipoNombre={`${selectedForAssignments.marca} ${selectedForAssignments.modelo}`}
          onClose={() => {
            setShowAssignmentTimeline(false);
            setSelectedForAssignments(null);
          }}
        />
      )}

      {showAISummary && (
        <InventoryAISummary
          tipo="tablets"
          onClose={() => setShowAISummary(false)}
        />
      )}
    </div>
  );
}

export default TabletsView;
